import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Menu, Bell, User, Settings, LogOut, HelpCircle, CreditCard, LayoutDashboard, Check, Trash2, X, Eye, ChevronDown, Search, Command, Coins, Play, KeyRound, ExternalLink, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTour } from '../../contexts/TourContext';
import { useNotifications } from '../../contexts/NotificationsContext';
import GlobalSearch from '../GlobalSearch';
import TourProgressChip from '../TourProgressChip';
import { BrandSwitcher } from '../BrandSwitcher';
import { SmartAlertsDropdown } from '../shared/SmartAlertsDropdown';
import { HubStatusDot } from '../HubStatusDot';

const API = process.env.REACT_APP_BACKEND_URL;
import ChangePasswordDialog from '../ChangePasswordDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';

// ─── Tiny inline brand-aware logo (listens to ChromeBrandProvider) ───
function BrandedLogo() {
  const [logo, setLogo] = useState(() => (typeof window !== 'undefined' && window.__KONNECT_BRAND_LOGO) || null);
  useEffect(() => {
    const handler = (e) => setLogo(e.detail || null);
    window.addEventListener('konnect:brand-logo', handler);
    return () => window.removeEventListener('konnect:brand-logo', handler);
  }, []);
  return logo
    ? <img src={logo} alt="Brand" className="h-8 w-auto object-contain max-w-[140px]" />
    : <img src="/konnect-logo-white.png" alt="Konnect" className="h-8 w-auto object-contain" />;
}

const NotificationItem = ({ notification, onMarkRead, onDelete }) => {
  const typeColors = {
    billing: 'bg-emerald-brand',
    billing_alert: 'bg-red-500',
    ai_task: 'bg-blue-500',
    social: 'bg-purple-500',
    review: 'bg-amber-500',
    default: 'bg-gray-500'
  };

  const color = typeColors[notification.notification_type] || typeColors.default;

  return (
    <div 
      className={cn(
        "p-3 border-b border-gray-border last:border-0 transition-colors",
        !notification.read && "bg-emerald-brand/5"
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn("w-2 h-2 rounded-full mt-2 flex-shrink-0", color)} />
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm font-medium text-text-primary", !notification.read && "font-semibold")}>
            {notification.title}
          </p>
          <p className="text-xs text-gray-medium mt-0.5">{notification.message}</p>
          <p className="text-xs text-gray-medium mt-1">
            {new Date(notification.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex gap-1">
          {!notification.read && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6"
              onClick={() => onMarkRead(notification.id)}
            >
              <Check className="h-3 w-3" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-6 w-6 text-gray-medium hover:text-red-500"
            onClick={() => onDelete(notification.id)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const Header = ({ onMenuClick }) => {
  const { user, logout, isViewingAs, viewAsRole, setViewAsRole, effectiveRole } = useAuth();
  const { startTour } = useTour();
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification, clearAll } = useNotifications();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [credits, setCredits] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const navigate = useNavigate();

  const API = process.env.REACT_APP_BACKEND_URL;

  // Fetch credits balance (for non-employee roles)
  const showCredits = ((effectiveRole && effectiveRole !== 'team_member') && user?.role !== 'team_member');
  useEffect(() => {
    if (!showCredits) return;
    // Fetch both platform (Kaharian) and local pool balance
    Promise.all([
      axios.get(`${API}/api/credits/balance`).catch(() => ({ data: {} })),
      axios.get(`${API}/api/credit-management/balance`).catch(() => ({ data: {} })),
    ]).then(([kaharianRes, poolRes]) => {
      const pool = poolRes.data;
      const kaharian = kaharianRes.data;
      // Prefer local pool if allocated, otherwise show Kaharian balance
      const localRemaining = (pool.allocated_credits || 0) - (pool.used_credits || 0);
      const kaharianBal = kaharian.remainingCredits || kaharian.balance || 0;
      setCredits({
        balance: localRemaining > 0 ? localRemaining : Math.round(kaharianBal),
        source: localRemaining > 0 ? 'pool' : 'platform',
      });
    });
    // Refresh balance every 30s for near-real-time updates
    const interval = setInterval(() => {
      Promise.all([
        axios.get(`${API}/api/credits/balance`).catch(() => ({ data: {} })),
        axios.get(`${API}/api/credit-management/balance`).catch(() => ({ data: {} })),
      ]).then(([kaharianRes, poolRes]) => {
        const pool = poolRes.data;
        const kaharian = kaharianRes.data;
        const localRemaining = (pool.allocated_credits || 0) - (pool.used_credits || 0);
        const kaharianBal = kaharian.remainingCredits || kaharian.balance || 0;
        setCredits({
          balance: localRemaining > 0 ? localRemaining : Math.round(kaharianBal),
          source: localRemaining > 0 ? 'pool' : 'platform',
        });
      });
    }, 30000);
    return () => clearInterval(interval);
  }, [API, showCredits]);

  // CMD+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <header 
      className="fixed top-0 left-0 right-0 h-16 bg-charcoal z-50 flex items-center justify-between px-4"
      data-testid="main-header"
    >
      {/* Left - Hamburger Menu + Logo */}
      <div className="flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onMenuClick}
          className="text-emerald-brand hover:bg-white/10 md:hidden"
          data-testid="hamburger-menu-btn"
        >
          <Menu className="h-6 w-6" />
        </Button>

        <Link to="/dashboard" className="flex items-center gap-2" data-testid="header-logo">
          <BrandedLogo />
        </Link>
      </div>

      {/* Center - Search Bar Trigger */}
      <button
        onClick={() => setSearchOpen(true)}
        className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 transition-colors text-gray-light max-w-xs flex-1 mx-4"
        data-testid="global-search-trigger"
      >
        <Search className="h-3.5 w-3.5 text-gray-400" />
        <span className="text-xs text-gray-400 flex-1 text-left">Search...</span>
        <kbd className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono text-gray-500 bg-white/10 rounded border border-white/10">
          <Command className="h-2.5 w-2.5" />K
        </kbd>
      </button>

      {/* Right - View As, Credits, Notifications & Profile */}
      <div className="flex items-center gap-0.5 sm:gap-1">
        {/* View As Dropdown (super_admin, desktop) */}
        {user?.role === 'super_admin' && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "hidden sm:flex items-center gap-1.5 text-xs h-8 px-2",
                  isViewingAs ? "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" : "text-gray-light hover:bg-white/10"
                )}
                data-testid="header-view-as-btn"
              >
                <Eye className="h-3.5 w-3.5" />
                <span>{isViewingAs ? (viewAsRole === 'country_operator' ? 'Operator' : 'Client') : 'View As'}</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => setViewAsRole(null)} className={cn("cursor-pointer", !isViewingAs && "bg-emerald-brand/5")}>
                Super Admin {!isViewingAs && <Check className="h-3 w-3 ml-auto" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setViewAsRole('country_operator')} className={cn("cursor-pointer", viewAsRole === 'country_operator' && "bg-emerald-brand/5")}>
                Country Operator {viewAsRole === 'country_operator' && <Check className="h-3 w-3 ml-auto" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setViewAsRole('client_admin')} className={cn("cursor-pointer", viewAsRole === 'client_admin' && "bg-emerald-brand/5")}>
                Client Admin {viewAsRole === 'client_admin' && <Check className="h-3 w-3 ml-auto" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Brand Switcher — Phase 5 Multi-Brand Agency Tier */}
        <BrandSwitcher />

        {/* Smart Alerts (AI-powered) */}
        <SmartAlertsDropdown />

        {/* Hub Status Dot — global, 60s polled */}
        <HubStatusDot />

        {/* Credits Balance Badge */}
        {showCredits && credits && (
          <button
            onClick={() => navigate('/settings/credits')}
            className="flex items-center gap-1 px-1.5 py-1 rounded-lg bg-white/10 hover:bg-white/15 transition-colors cursor-pointer"
            data-testid="header-credits-badge"
            title="AI Credits — click to manage"
          >
            <Coins className="h-3.5 w-3.5 text-amber-400" />
            <span className="text-xs font-bold text-amber-400">{(credits.balance || 0).toLocaleString()}</span>
          </button>
        )}
        <TourProgressChip />
        <Sheet open={notificationsOpen} onOpenChange={setNotificationsOpen}>
          <SheetTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-gray-light hover:bg-white/10 relative"
              data-testid="notifications-btn"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:w-[400px] p-0">
            <SheetHeader className="p-4 border-b border-gray-border">
              <div className="flex items-center justify-between gap-2">
                <SheetTitle className="font-heading text-base flex-shrink-0">Notifications</SheetTitle>
                <div className="flex items-center gap-1 flex-shrink-0 mr-6">
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs px-2 h-7">
                      Mark read
                    </Button>
                  )}
                  {notifications.length > 0 && (
                    <Button variant="ghost" size="sm" className="text-red-500 text-xs px-2 h-7" onClick={clearAll}>
                      Clear all
                    </Button>
                  )}
                </div>
              </div>
            </SheetHeader>
            <ScrollArea className="h-[calc(100vh-80px)]">
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkRead={markAsRead}
                    onDelete={deleteNotification}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-medium">
                  <Bell className="h-12 w-12 mb-4 text-gray-300" />
                  <p>No notifications yet</p>
                  <p className="text-sm">We'll notify you when something happens</p>
                </div>
              )}
            </ScrollArea>
          </SheetContent>
        </Sheet>

        {/* Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="hover:bg-white/10"
              data-testid="profile-dropdown-trigger"
            >
              <Avatar className="h-8 w-8 border-2 border-emerald-brand">
                <AvatarFallback className="bg-emerald-brand text-white text-sm">
                  {getInitials(user?.full_name)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-3 py-2">
              <p className="text-sm font-medium">{user?.full_name || 'User'}</p>
              <p className="text-xs text-gray-medium">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings" className="flex items-center gap-2 cursor-pointer" data-testid="profile-settings-link">
                <Settings className="h-4 w-4" />
                Account Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                <LayoutDashboard className="h-4 w-4" />
                My Dashboard
              </Link>
            </DropdownMenuItem>
            {/* v3.16 — Top Up Credits is a Subscriber-Admin function; hide from employees */}
            {user?.role !== 'team_member' && <DropdownMenuItem asChild>
              <Link to="/settings/credits" className="flex items-center gap-2 cursor-pointer">
                <CreditCard className="h-4 w-4" />
                Top Up Credits
              </Link>
            </DropdownMenuItem>}
            <DropdownMenuItem asChild>
              <Link to="/usage" className="flex items-center gap-2 cursor-pointer">
                <Bell className="h-4 w-4" />
                Usage Analytics
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/help" className="flex items-center gap-2 cursor-pointer">
                <HelpCircle className="h-4 w-4" />
                Help & Support
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={async () => {
                try {
                  await axios.post(`${API}/api/onboarding/load-demo-data`);
                  await axios.post(`${API}/api/tour/reset`).catch(() => {});
                  toast.success('Demo data ready — starting the tour…');
                  setTimeout(() => { startTour({ reset: true }); }, 800);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Could not start replay');
                }
              }}
              className="flex items-center gap-2 cursor-pointer"
              data-testid="replay-onboarding-item"
            >
              <Play className="h-4 w-4" />
              Replay onboarding tour
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={async () => {
                if (!window.confirm('Remove the demo data (3 demo brands, drafts, reviews, metrics)? Your real data is untouched.')) return;
                try {
                  const { data } = await axios.delete(`${API}/api/onboarding/clear-demo-data`);
                  const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
                  toast.success(`Cleared ${total} demo records`);
                  setTimeout(() => window.location.reload(), 600);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Could not clear demo data');
                }
              }}
              className="flex items-center gap-2 cursor-pointer text-rose-600"
              data-testid="clear-demo-data-item"
            >
              <Trash2 className="h-4 w-4" />
              Clear demo data
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setChangePasswordOpen(true)}
              className="flex items-center gap-2 cursor-pointer"
              data-testid="change-password-menu-item"
            >
              <KeyRound className="h-4 w-4" />
              Change Password
            </DropdownMenuItem>
            {user?.role === 'country_operator' && (
              <DropdownMenuItem
                onClick={async () => {
                  try {
                    const { data } = await axios.get(`${API}/api/auth/preferred-host`);
                    const target = data?.preferred_host;
                    const token = localStorage.getItem('konnect_access_token');
                    if (target && target !== window.location.host) {
                      window.open(`https://${target}/login?token=${encodeURIComponent(token || '')}`, '_blank', 'noopener');
                    } else {
                      toast.info('You\'re already on your customer-facing URL.');
                    }
                  } catch {
                    toast.error('Could not resolve your branded URL.');
                  }
                }}
                className="flex items-center gap-2 cursor-pointer"
                data-testid="view-as-customer-menu-item"
              >
                <ExternalLink className="h-4 w-4" />
                View as my customer
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                if (!window.confirm('Sign out of every device including this one? You will need to log in again everywhere.')) return;
                try {
                  await axios.post(`${API}/api/auth/logout-all-devices`);
                  toast.success('All sessions revoked. Signing out…');
                  setTimeout(() => logout(), 800);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Could not revoke sessions. Please try again.');
                }
              }}
              className="text-amber-600 cursor-pointer"
              data-testid="logout-all-devices-btn"
            >
              <Shield className="h-4 w-4 mr-2" />
              Sign Out of All Devices
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={logout} 
              className="text-red-500 cursor-pointer"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Global Search Dialog */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Change Password Dialog (self-service from profile menu) */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </header>
  );
};

export default Header;
